import {Injectable} from "@angular/core";
import {CompetitorService} from "./competitor.service";
import {LocalStorageService} from "./local-storage.service";
import {
    Competitor,
    CompetitorSaveControlPointResultStatus,
    ControlPointTime,
    MeasurementTimeItemHolder,
    WaypointType
} from "@model";
import {BehaviorSubject, catchError, forkJoin, Observable, of, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';
import {v4 as uuidv4} from "uuid";
import {MeasurementsService} from "@services";

@Injectable({providedIn: 'root'})
export class MeasurementTimeItemHolderService {

    private _currentHolder = new BehaviorSubject<MeasurementTimeItemHolder[] | undefined>(undefined);

    private static readonly WAYPOINT_EVENT_HOLDER_KEY = "WAYPOINT_EVENT_HOLDER_KEY";

    get currentHolder() {
        return this._currentHolder;
    }

    constructor(
        private competitorService: CompetitorService,
        private localStorageService: LocalStorageService,
        private measurementsService: MeasurementsService
    ) {
    }


    private createKey = (measurementsPointsView: boolean, eventId: number) => `event-${measurementsPointsView ? 'checkpoints' : 'laps'}-${eventId}`;
    private createWaypointKey = (measurementsPointsView: boolean, eventId: number, waypointId?: number) => `timing-event-${measurementsPointsView ? 'checkpoints' : 'laps'}-${eventId}${waypointId ? 'waypoint-'+waypointId : ''}`;


    saveHolderForEvent(measurementsPointsView: boolean, eventId: number, data: string) {
        this.localStorageService.saveData(
            this.createKey(measurementsPointsView, eventId), data
        );
    }


    deleteHolderForEvent(measurementsPointsView: boolean, eventId: number) {
        this.localStorageService.removeData(this.createKey(measurementsPointsView, eventId));
    }

    getHolderForEvent(measurementsPointsView: boolean, eventId: number, update?: boolean): Observable<MeasurementTimeItemHolder[]> {
        const value = this.localStorageService.getData(this.createKey(measurementsPointsView, eventId));
        if (!value) return of([]);

        const measurementTimeItemHolders: MeasurementTimeItemHolder[] = JSON.parse(value);
        if (!update) return of(measurementTimeItemHolders);

        const uniqueCompetitorIds = Array.from(
            new Set(measurementTimeItemHolders
                .filter(holder => holder.competitor?.id)
                .map(holder => holder.competitor!.id!))
        );

        if (uniqueCompetitorIds.length === 0) {
            return of(measurementTimeItemHolders);
        }

        return forkJoin(uniqueCompetitorIds.map(id =>
            this.competitorService.fetchCompetitorById(id).pipe(
                switchMap(updatedCompetitor => {
                    return !measurementsPointsView && updatedCompetitor?.raceId
                        ? this.measurementsService.findLapsTimes(id, updatedCompetitor.raceId).pipe(
                            map((laps:ControlPointTime[])  => {
                                const filteredLaps = laps.filter(lap => !(lap.controlWaypoint && lap.controlWaypoint.waypointTypeId === WaypointType.START_WAYPOINT));
                                return { id, updatedCompetitor: { ...updatedCompetitor, laps: filteredLaps } };
                            }
                        ))
                        : of({ id, updatedCompetitor });
                }),
                catchError(() => of({ id, updatedCompetitor: null }))
            )
        )).pipe(
            map(competitorUpdates => {
                const competitorMap = new Map(competitorUpdates
                    .filter(({ updatedCompetitor }) => updatedCompetitor)
                    .map(({ id, updatedCompetitor }) => [id, updatedCompetitor!])
                );

                return measurementTimeItemHolders.map(holder => {
                    const updatedCompetitor = holder.competitor?.id ? competitorMap.get(holder.competitor.id) : holder.competitor;
                    return {
                        ...holder,
                        key: uuidv4(),
                        saveResult: CompetitorSaveControlPointResultStatus.OK,
                        competitor: updatedCompetitor,
                        startNumber: updatedCompetitor?.startNumber ?? holder.competitor?.startNumber,
                    };
                });
            })
        );
    }


    saveHolderForEventCheckPoint(measurementsPointsView: boolean, eventId: number, waypointId: number | undefined, data: MeasurementTimeItemHolder[]) {
        this._currentHolder.next(data)
        const key = this.createWaypointKey(measurementsPointsView, eventId, waypointId)
        this.saveKey(key);
        this.localStorageService.saveData(key, JSON.stringify(data.map(item => ({...item, subscription: undefined, inProgress: false}))
        ));
    }

    deleteHolderForEventCheckPoint(measurementsPointsView: boolean, eventId: number, waypointId: number | undefined) {
        this._currentHolder.next(undefined)
        this.localStorageService.removeData(this.createWaypointKey(measurementsPointsView, eventId, waypointId));
    }


    initFromCache(measurementsPointsView: boolean, eventId: number, waypointId: number | undefined) {
        const value = this.localStorageService.getData(this.createWaypointKey(measurementsPointsView, eventId, waypointId));
        if (!value) {
            this._currentHolder.next(undefined);
            return;
        }

        const measurementTimeItemHolders: MeasurementTimeItemHolder[] = JSON.parse(value);

        // Key on startNumber+extension together — a bare startNumber can be shared
        // across multiple team members, so the extension is what disambiguates them.
        const uniqueLookups = [
            ...new Map(
                measurementTimeItemHolders
                    .filter((item): item is MeasurementTimeItemHolder & { startNumber: string } => !!item.startNumber)
                    .map(item => [`${item.startNumber}::${item.startNumberExtension ?? ''}`, {startNumber: item.startNumber, extension: item.startNumberExtension}])
            ).values()
        ];

        this.measurementsService.ping().pipe(
            switchMap(isOnline => {
                if (!isOnline || uniqueLookups.length === 0) {
                    return of([]);
                }
                const requests = uniqueLookups.map(({startNumber, extension}) =>
                    this.competitorService.fetchCompetitor(
                        startNumber,
                        undefined,
                        eventId,
                        waypointId,
                        false,
                        extension
                    )
                );

                return forkJoin(requests);
            })
        ).subscribe((competitors: (Competitor | undefined)[]) => {
            const competitorMap = new Map<string, Competitor>();
            competitors.forEach(comp => {
                if (comp?.startNumber) {
                    competitorMap.set(`${comp.startNumber}::${comp.startNumberExtension ?? ''}`, comp);
                }
            });

            measurementTimeItemHolders.forEach(item => {
                if (item.startNumber) {
                    const updated = competitorMap.get(`${item.startNumber}::${item.startNumberExtension ?? ''}`);
                    if (updated) {
                        const savedData = item.competitor;
                        item.competitor = { ...updated, ...savedData };
                    }
                }
            });
            this._currentHolder.next(measurementTimeItemHolders);
        });
    }

    getKeys(): string[] {
        const value = this.localStorageService.getData(MeasurementTimeItemHolderService.WAYPOINT_EVENT_HOLDER_KEY);
        if (!value) {
            return [];
        }
        else {
            const keys: string[] = JSON.parse(value);
            return keys;
        }
    }

    saveKey(key: string) {
        const keysString = this.localStorageService.getData(MeasurementTimeItemHolderService.WAYPOINT_EVENT_HOLDER_KEY);
        const keysSet: Set<string> = new Set(keysString ? JSON.parse(keysString) : []);
        keysSet.add(key);
        this.localStorageService.saveData(
            MeasurementTimeItemHolderService.WAYPOINT_EVENT_HOLDER_KEY,
            JSON.stringify(Array.from(keysSet))
        );

    }

    empty() {
        this._currentHolder.next(undefined)
    }

    deleteAllEventHolders() {
        for (let key of this.getKeys()) {
            this.localStorageService.removeData(key)
        }
        this.localStorageService.removeData(MeasurementTimeItemHolderService.WAYPOINT_EVENT_HOLDER_KEY);
    }


}


